import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";
import {
    PortfolioRewards,
    PortfolioRewardsFilter,
    RefinanceConstraintResponse,
    SetRefinanceConstraintParams,
    ToggleLoanPositionParams
} from "../types";
import {
    GET_LOAN_BY_ADDRESS_ROUTE,
    ROUTE_PORTFOLIO_REWARDS,
    ROUTE_SET_REFINANCE_CONSTRAINT,
    ROUTE_TOGGLE_LOAN_POSITION
} from "../constants";

const MARKETS_TAG = "Markets";
const PORTFOLIO_REWARDS_TAG = "PortfolioRewards";
const REFINANCE_CONSTRAINT_TAG = "RefinanceConstraint";

export const marketsApi = createApi({
    reducerPath: "marketsApi",
    tagTypes: [MARKETS_TAG, PORTFOLIO_REWARDS_TAG, REFINANCE_CONSTRAINT_TAG],
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,

    endpoints: (builder) => ({
        portfolioRewards: builder.query<PortfolioRewards, PortfolioRewardsFilter>({
            providesTags: [PORTFOLIO_REWARDS_TAG],
            query(filter) {
                return {
                    url: ROUTE_PORTFOLIO_REWARDS,
                    method: "POST",
                    body: filter
                };
            }
        }),
        toggleLoanPosition: builder.mutation<void, ToggleLoanPositionParams>({
            invalidatesTags: [MARKETS_TAG],
            query(params) {
                return {
                    url: ROUTE_TOGGLE_LOAN_POSITION,
                    method: "POST",
                    body: params
                };
            }
        }),
        refinanceConstraint: builder.query<RefinanceConstraintResponse, string>({
            providesTags: [REFINANCE_CONSTRAINT_TAG],
            query(loanAddress) {
                return {
                    url: `${GET_LOAN_BY_ADDRESS_ROUTE}/${loanAddress}/refinance_constraint`,
                    method: "GET"
                };
            }
        }),
        setRefinanceConstraint: builder.mutation<void, SetRefinanceConstraintParams>({
            invalidatesTags: [REFINANCE_CONSTRAINT_TAG, MARKETS_TAG],
            query(params) {
                return {
                    url: ROUTE_SET_REFINANCE_CONSTRAINT,
                    method: "POST",
                    body: params
                };
            }
        })
    })
});

export const {
    usePortfolioRewardsQuery,
    useToggleLoanPositionMutation,
    useRefinanceConstraintQuery,
    useSetRefinanceConstraintMutation
} = marketsApi;

export const useMarketsApi = () => {
    const dispatch = useDispatch();

    return {
        resetRewardsApi: () => dispatch(marketsApi.util.invalidateTags([PORTFOLIO_REWARDS_TAG])),
        resetNapoleonApi: () => dispatch(marketsApi.util.invalidateTags([MARKETS_TAG]))
    };
};
